import useModals from "@actions/useModals";
import useNotifications from "@actions/useNotifications";
import {
  Item,
  KeyboardShortcut,
  Kit,
  PopupMenu,
  Portal,
  Section,
  Space,
  useNavigation,
} from "@thenounproject/lingo-core";
import useBatchUpdateItemStatus from "@actions/items/useBatchUpdateItemStatus";
import useDuplicateItems from "@actions/items/useDuplicateItems";
import useMoveItems from "@actions/items/useMoveItems";
import useBatchSaveItems from "@actions/items/useBatchSaveItems";
import useUpdateItem from "@actions/items/useUpdateItem";
import useSaveMultipleAssetTags from "@actions/assets/useSaveMultipleAssetTags";
import useReplaceAssetFile from "@actions/assets/useReplaceAssetFile";
import useCreateItemAsset from "@actions/items/useCreateItemAsset";
import useReplaceAssetFileFromSource from "@actions/assets/useReplaceAssetFileFromSource";
import useCreateAssetsFromSource from "@actions/items/useCreateAssetsFromSource";
import useBatchProcessAssets from "@actions/assets/useBatchProcessAssets";
import useRemoveAssetThumbnail from "@actions/assets/useRemoveAssetThumbnail";
import { useAssetDownload } from "@features/asset-export/hooks/useAssetDownload";
import { useAssetCopy } from "@features/asset-export/hooks/useAssetCopy";
import useDeleteAssets from "@actions/assets/useDeleteAssets";
import useDeleteAssetItems from "@actions/assets/useDeleteAssetItems";
import { useSelectionActions } from "@src/contexts/useSelectionHooks";
import { Inspectable } from "@constants/Inspector";
import { NavPointTypes } from "@src/@redux/navPoint/NavPoint";
import { InspectorModes } from "@src/contexts/ViewModeContext";
import useBatchUpdateAssetsMetadata from "@actions/assets/useBatchUpdateAssetsMetadata";
import useSaveAssetMetadata from "@actions/assets/useSaveAssetMetadata";
import { ComponentProps } from "react";

/**
 * Declarative action definition.
 * This is the static definition of an action, separate from its runtime state.
 */
export interface ActionDefinition {
  /** Unique identifier for the action */
  id: string;

  /** Display label - can be static or computed from context */
  label: DynamicAttribute<string>;

  /** Optional description of what the action does */
  description?: string;

  /** Search synonyms for fuzzy matching */
  synonyms?: string[];

  /** Icon identifier - can be static or computed from context */
  icon?: DynamicAttribute<string>;

  image?: DynamicAttribute<ComponentProps<typeof PopupMenu.Item>["image"]>;

  /** Category for grouping in the menu */
  category: ActionCategory;

  /**
   * Visibility rules - declarative predicates that determine when action is available.
   * All predicates must return true for the action to be enabled.
   */
  visibility: {
    /** Predicates that determine if action is enabled */
    predicates: ActionPredicate[];
    /** Predicates that determine if action shows without search input (defaults to same as predicates if not specified) */
    defaultPredicates?: ActionPredicate[];
  };

  /** Keyboard shortcut for triggering this action */
  shortcut?: Omit<KeyboardShortcut, "displayString">;

  /** Whether selecting this action should close the menu */
  closeOnSelect?: boolean;

  /** Whether selecting this action should clear the search input */
  clearInputOnSelect?: boolean;

  /**
   * The action performer function.
   * Called when the action is executed, receives context and dependencies.
   * Optional during migration - if not provided, falls back to PerformerMap lookup.
   */
  perform: (context: ActionContext, deps: ActionDeps) => void | Promise<void>;
}

export interface Action {
  // unique ID for the action
  id: string;
  // The title of the action, displayed in the menu
  label: string;
  // A short description of what the action does, (not used currently)
  description?: string;
  // Other words to match against the user input
  synonyms?: string[];
  // Whether the action can be displayed or not
  enabled: boolean;
  // Whether the action is the default action when no input is given
  default?: boolean;
  // Icon to be shown in the menu
  icon?: string;
  image?: ComponentProps<typeof PopupMenu.Item>["image"];
  // The category of the action, used for grouping in the menu
  category: ActionCategory;
  // Whether the action should close the menu when selected
  closeOnSelect?: boolean;
  clearInputOnSelect?: boolean;
  // The function to call when the action is selected
  perform: () => Promise<void>;
}

/**
 * Extended Action type with shortcut info.
 */
export type ActionWithShortcut = Action & {
  shortcut?: KeyboardShortcut;
  shortcutDisplay?: string;
};

export type ActionFactory = (context: ActionContext) => ActionDefinition[];

export type LocationContext = {
  type: NavPointTypes;
  inLibrary: boolean;
  inAssetDetail: boolean;
  inGallery: boolean;
  inKit: boolean;

  kit?: Kit;
  gallery: Item;
  space: Space;
  portal?: Portal;
};

export type CapabilitiesContext = {
  canEdit: boolean;
};

export type SelectionContext = {
  all: Inspectable[];
  /** Any inspectable with an item */
  items: Inspectable[];
  /**  Any inspectable with an asset */
  assets: Inspectable[];
  /** Item.type === ItemType.asset */
  assetItems: Inspectable[];
  /** Item.type === ItemType.note  */
  noteItems: Inspectable[];
  /** Item.type === ItemType.guide  */
  guideItems: Inspectable[];
  /** Item.type === ItemType.heading  */
  headingItems: Inspectable[];
  /** Item.type === ItemType.gallery  */
  galleryItems: Inspectable[];
  /** Item.type === ItemType.code  */
  codeItems: Inspectable[];
  /** asset.type is a motion asset (with or without an item)  */
  motionAssets: Inspectable[];

  /** The item is a resizable type  */
  resizableItems: Inspectable[];
  /** The item is compatible with card backgrounds  */
  cardBackgroundCompatibleItems: Inspectable[];
};

export type ActionContext = {
  // User interaction
  userInput: string;

  // Location/navigation context
  location: LocationContext;

  // Computed capabilities
  capabilities: CapabilitiesContext;

  // Pre-filtered selection groups
  selection: SelectionContext;

  // Current state of the app interface
  interface: {
    inspectorMode: InspectorModes;
  };

  // Data pulled from the redux store
  data: {
    sections?: Record<string, Section>;
  };
};

export type ActionCreator = (context: ActionContext) => Action[];

export enum ActionCategory {
  View = "View",
  Update = "Update",
  Display = "Display",
  Manage = "Manage",
}

const ActionCategoryOrder: Record<ActionCategory, number> = {
  [ActionCategory.View]: 1,
  [ActionCategory.Update]: 2,
  [ActionCategory.Display]: 3,
  [ActionCategory.Manage]: 4,
};

// Predicate function for determining action visibility
export type ActionPredicate = (context: ActionContext) => boolean;

// Label can be static or computed from context
export type DynamicAttribute<T> = T | ((context: ActionContext) => T);

/**
 * Dependencies available to action performers.
 * These are provided by useActionDeps and passed to each action's perform function.
 *
 * Note: Types are kept loose here to avoid circular dependencies and complex imports.
 * The actual types are enforced in useActionDeps.ts where the hooks are initialized.
 */
export interface ActionDeps {
  // Modals & Notifications
  showModal: ReturnType<typeof useModals>["showModal"];
  dismissModal: ReturnType<typeof useModals>["dismissModal"];
  showNotification: ReturnType<typeof useNotifications>["showNotification"];

  // Navigation
  navigation: ReturnType<typeof useNavigation>;
  portalId: string | undefined;

  // Item mutations
  batchUpdateItemStatus: ReturnType<typeof useBatchUpdateItemStatus>[0];
  duplicateItems: ReturnType<typeof useDuplicateItems>[0];
  moveItems: ReturnType<typeof useMoveItems>[0];
  batchSaveItems: ReturnType<typeof useBatchSaveItems>[0];
  updateItem: ReturnType<typeof useUpdateItem>[0];

  // Asset mutations
  batchSaveTags: ReturnType<typeof useSaveMultipleAssetTags>[0];
  replaceAssetFile: ReturnType<typeof useReplaceAssetFile>[0];
  createItemAsset: ReturnType<typeof useCreateItemAsset>[0];
  replaceAssetFileFromSource: ReturnType<typeof useReplaceAssetFileFromSource>[0];
  createAssetFromSource: ReturnType<typeof useCreateAssetsFromSource>[0];
  batchProcessAssets: ReturnType<typeof useBatchProcessAssets>[0];
  removeAssetThumbnail: ReturnType<typeof useRemoveAssetThumbnail>[0];
  deleteAssets: ReturnType<typeof useDeleteAssets>[0];
  deleteAssetItems: ReturnType<typeof useDeleteAssetItems>[0];
  batchUpdateAssetsMetadata: ReturnType<typeof useBatchUpdateAssetsMetadata>[0];
  saveAssetMetadata: ReturnType<typeof useSaveAssetMetadata>[0];

  // Downloads
  assetDownload: ReturnType<typeof useAssetDownload>;
  assetCopy: ReturnType<typeof useAssetCopy>;

  // View mode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setInspectorMode: ((mode: any) => void) | undefined;

  // Selection-dependent actions (may be undefined if not available)
  selection: ReturnType<typeof useSelectionActions>;
  editHandler: ((itemId: string) => void) | undefined;
}

/**
 * Factory function for creating dynamic actions.
 * Used for actions that need to be generated based on context (e.g., color formats, download presets).
 */

export function getActionCategoryOrder(category: ActionCategory): number {
  return ActionCategoryOrder[category] ?? 999;
}

/**
 * Helper to resolve dynamic label from context.
 */
export function resolveDynamicAttribute<T>(attr: DynamicAttribute<T>, context: ActionContext): T {
  return typeof attr === "function" ? (attr as (context: ActionContext) => T)(context) : attr;
}

// /**
//  * Helper to resolve dynamic icon from context.
//  */
// export function resolveIcon(
//   icon: DynamicIcon | undefined,
//   context: ActionContext
// ): string | undefined {
//   if (!icon) return undefined;
//   return typeof icon === "function" ? icon(context) : icon;
// }

/**
 * Build the modifier prefix for shortcut key strings.
 */
function buildModifierPrefix(modifiers: {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}): string {
  const parts: string[] = [];
  if (modifiers.ctrl) parts.push("ctrl");
  if (modifiers.alt) parts.push("alt");
  if (modifiers.shift) parts.push("shift");
  if (modifiers.meta) parts.push("meta");
  return parts.length > 0 ? parts.join("+") + "+" : "";
}

/**
 * Convert a keyboard event to shortcut key strings for matching.
 * Returns both key-based and code-based strings to match against either type.
 */
export function eventToShortcutKeys(event: KeyboardEvent): { keyBased: string; codeBased: string } {
  const prefix = buildModifierPrefix({
    ctrl: event.ctrlKey,
    alt: event.altKey,
    shift: event.shiftKey,
    meta: event.metaKey,
  });

  return {
    keyBased: prefix + "key:" + event.key.toLowerCase(),
    codeBased: prefix + "code:" + event.code.toLowerCase(),
  };
}

/**
 * @deprecated Use eventToShortcutKeys instead
 */
export function eventToShortcutKey(event: KeyboardEvent): string {
  return eventToShortcutKeys(event).keyBased;
}

/**
 * Convert a KeyboardShortcut to a key string for matching.
 */
export function shortcutToKey(shortcut: KeyboardShortcut): string {
  const prefix = buildModifierPrefix({
    ctrl: shortcut.modifiers?.includes("ctrl") ?? false,
    alt: shortcut.modifiers?.includes("alt") ?? false,
    shift: shortcut.modifiers?.includes("shift") ?? false,
    meta: shortcut.modifiers?.includes("meta") ?? false,
  });

  // Use code if provided, otherwise use key
  if (shortcut.code) {
    return prefix + "code:" + shortcut.code.toLowerCase();
  }
  return prefix + "key:" + (shortcut.key?.toLowerCase() ?? "");
}
